import React, { useState, useEffect, useMemo, useRef } from "react";
// اگر TreeNode.jsx باشد، import را به این صورت تغییر دهید:
// import TreeNode from "./TreeNode.jsx"; 
import TreeNode from "./TreeNode"; // فرض می‌کنیم نام فایل TreeNode.jsx است
import './TreeView.css';

/**
 * @typedef {object} Node
 * @property {string} id
 * @property {string} name
 * @property {string} [code]
 * @property {Node[]} [children]
 */
/**
 * 
 * @param {object} props
 * @param {Node} props.nodes
 * @param {function} props.setNodes
 * @param {function} props.setSelectedNode
 * @param {function} props.onAddNode
 * @param {function} props.onEditNode
 * @param {function} props.onRemoveNode
 * @returns 
 */

const TreeView = ({ nodes, setNodes, setSelectedNode, onAddNode, onRemoveNode, onEditNode }) => {

const searchTimeout = useRef();
  const [expanded, setExpanded] = useState({ root: true });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // --- تابع کمکی برای باز کردن خودکار گره‌های جستجو شده ---
  // این تابع باید گره‌هایی که حاوی نتیجه جستجو هستند و والدینشان را پیدا و باز کند
  const findAllMatchingNodes = (node, searchTerm) => {
    const results = [];
    const lowerSearch = searchTerm.toLowerCase();
    const nameMatch = node.name.toLowerCase().includes(lowerSearch);
    const codeMatch = node.code?.toLowerCase().includes(lowerSearch);

    let currentNodeMatches = nameMatch || codeMatch;

    if (node.children) {
      for (const child of node.children) {
        const childResults = findAllMatchingNodes(child, searchTerm);
        if (childResults.length > 0) {
          currentNodeMatches = true; // اگر فرزندانش نتیجه داشتن، این گره هم باید باز شود
          results.push(...childResults);
        }
      }
    }

    if (currentNodeMatches) {
      // اگر گره فعلی یا فرزندانش مطابقت داشتند، ID گره فعلی را به نتایج اضافه کن
      // فقط اگر گره روت نباشد (چون همیشه باز است)
      if (node.id !== "root") {
        results.push(node.id);
      }
    }
    return results;
  };

  const handleSearchEvent =(search) => {
    if(searchTimeout.current)
    {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
      if (search.trim()) {
        // وقتی سرچ فعال است، گره‌های مرتبط را پیدا و باز کن
        const matchingNodeIds = findAllMatchingNodes(nodes, search);
        const newExpandedState = { ...expanded };
        matchingNodeIds.forEach(id => {
          newExpandedState[id] = true;
        });
        setExpanded(newExpandedState);
      } else {
        setExpanded({ root: true });
      }
    }, 350);
  };

  // --- تابع فیلتر کردن درخت (اصلاح شده) ---
  // این تابع فقط گره‌هایی را نگه می‌دارد که یا خودشان مطابقت دارند یا فرزندانشان مطابقت دارند
  const filterTree = (node, searchTerm) => {
    const lower = searchTerm.toLowerCase();
    const nameMatch = node.name.toLowerCase().includes(lower);
    const codeMatch = node.code?.toLowerCase().includes(lower);

    // اگر گره فعلی مطابقت دارد، آن را با فرزندان فیلتر شده‌اش برگردان
    if (nameMatch || codeMatch) {
      const children = node.children
        ?.map(child => filterTree(child, searchTerm))
        .filter(Boolean); // حذف null ها
      return { ...node, children };
    }

    // اگر گره فعلی مطابقت ندارد، بررسی کن که آیا فرزندانش مطابقت دارند یا خیر
    const matchingChildren = node.children
      ?.map(child => filterTree(child, searchTerm))
      .filter(Boolean); // فقط فرزندانی که نتیجه دارند یا فرزندانشان نتیجه دارند را نگه دار

    // اگر فرزندان منطبقی وجود داشت، این گره را با آن فرزندان برگردان
    if (matchingChildren && matchingChildren.length > 0) {
      return { ...node, children: matchingChildren };
    }

    // اگر نه خود گره و نه هیچ یک از فرزندانش مطابقت ندارند، null برگردان
    return null;
  };

  const filteredTree = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return nodes; // اگر جستجو خالی است، کل درخت را برگردان
    }
    // درخت را فیلتر کن
    const result = filterTree(nodes, debouncedSearch);
    // اگر نتیجه‌ای نبود، یک پیام نمایش بده
    return result || { id: "empty", name: "هیچ نتیجه‌ای یافت نشد" };
  }, [debouncedSearch, nodes]);

  // --- توابع مدیریت state (addNode, removeNode, toggleExpand) - بدون تغییر ---
  const addNode = async (parentId, name, code) => {
    const newId = `node-${Date.now()}`;
    const newNode = { id: newId, name, code };

    setNodes((current) => {
      const clone = structuredClone(current);
      function insert(node) {
        if (node.id === parentId) {
          node.children = node.children || [];
          node.children.push(newNode);
          return true;
        }
        return node.children?.some(insert) || false;
      }

      if (typeof onAddNode === "function") {
        const result = onAddNode({ parentId: parentId, name: name, code: code });
        if (result && typeof result.then === "function") {
          result.then(res => {
            if (res) {
              newNode.id = res;
              insert(clone);
            }
          });
        }
        else {
          insert(clone);
        }
      } else {
        insert(clone);
      }
      return clone;
    });
    // هنگام اضافه کردن، والد را باز می‌کنیم
    setExpanded((prev) => ({ ...prev, [parentId]: true }));
  };
  const editNode = (id, name, code) => {

    setNodes((current) => {
      const clone = structuredClone(current);
      function update(node) {
        if (node.id === id) {
          node.name = name;
          node.code = code;
          return true;
        }
        return node.children?.some(update) || false;
      }
      update(clone);
      if (typeof onEditNode === "function") {
        onEditNode({ id: id, name: name, code: code });
      }
      return clone;
    });
  };

  const removeNode = (targetId) => {
    setNodes((current) => {
      const clone = structuredClone(current);
      function findAndRemove(node) {
        if (!node.children) return false;
        const idx = node.children.findIndex((child) => child.id === targetId);
        if (idx !== -1) {
          const target = node.children[idx];
          const kids = target.children || [];
          node.children.splice(idx, 1, ...kids);
          return true;
        }
        return node.children.some(findAndRemove);
      }
      if (clone.id === targetId) {
        const kids = clone.children || [];
        return { id: "root", name: "ریشه جدید", children: kids };
      }
      findAndRemove(clone);
      if (typeof onRemoveNode === "function") {
        onRemoveNode(targetId);
      }
      return clone;
    });
    // وقتی گره‌ای حذف می‌شود، باید آن ID را از expanded هم حذف کنیم
    setExpanded(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[targetId];
      return newExpanded;
    });
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <div className="tree-view-container">
      <div className="tree-header">
        <input
          type="text"
          placeholder="جستجو (نام یا کد)..."
          onChange={(e) => handleSearchEvent(e.target.value)}
          className="search-input"
        />
      </div>
      <TreeNode
        node={filteredTree}
        expanded={expanded}
        onToggleExpand={toggleExpand}
        onAddNode={addNode}
        onRemoveNode={removeNode}
        onEditNode={editNode}
        search={debouncedSearch}
        onSelectNode={setSelectedNode}
      />
    </div>
  );
};

export default TreeView;
